import { 
  Card, 
  CardContent, 
  Grid, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem,
  Paper, 
  Button, 
  Typography, 
  Box, 
  TextField, 
  Chip, 
  Avatar,
  IconButton,
  InputAdornment,
  alpha,
  Divider
} from "@mui/material";
import { DatePicker } from '@mui/x-date-pickers';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { 
  Save, 
  Cancel, 
  Delete, 
  Add, 
  Remove, 
  MenuBook,
  Event,
  Restaurant,
  AttachMoney,
  TrendingUp,
  Close
} from "@mui/icons-material";
import { useState } from 'react';
import { format } from 'date-fns'; 
import DailyServings from "../../Services/DailyServing";
import { motion, AnimatePresence } from "framer-motion";

// Animation variants
const fadeInUp = {
  initial: { opacity: 0, y: 10 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -10 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.05
    }
  }
};

export const DailyServingForm = ({
  products,
  selectedProducts,
  currentServing,
  handleProductSelect,
  updateQuantity,
  removeProduct,
  resetServingForm
}) => {
  const [setDailyServings] = useState([]);
  const restaurantId = localStorage.getItem("restaurantId");
  const [currentDate, setCurrentDate] = useState(new Date());

  const fetchDailyServings = async () => {
    try {
      const data = await DailyServings.getDailyServingsByRestaurantId(restaurantId);
      const normalizedData = data.map(serving => ({
        id: serving.id,
        items: serving.items || [],
        totalCost: parseFloat(serving.totalCost) || 0,
        realCost: parseFloat(serving.realCost) || 0,
        date: serving.date || new Date().toISOString(),
        restaurantId: serving.restaurantId
      }));
      setDailyServings(normalizedData);
    } catch (error) {
      console.error("Error fetching daily servings:", error);
    }
  };

  const addDailyServing = async () => {
    try {
      if (selectedProducts.length === 0) {
        throw new Error("Please select at least one product");
      }

      const items = selectedProducts.map(p => ({
        productId: p.id,
        productName: p.name,
        quantity: p.quantity,
        costPrice: p.costPrice,
        soldPrice: p.soldPrice
      }));

      const totalCost = items.reduce((sum, item) => sum + (item.costPrice * item.quantity), 0);
      const realCost = items.reduce((sum, item) => sum + (item.soldPrice * item.quantity), 0);
      
      const date = currentDate.toISOString();

      const newServing = new DailyServings(
        items,
        totalCost,
        realCost,
        date,
        restaurantId
      );

      await newServing.addDailyServing();
      await fetchDailyServings();
      resetServingForm();
    } catch (error) {
      console.error("Error saving daily serving:", error);
      throw error;
    }
  };

  const handleSubmit = async () => {
    try {
      if (selectedProducts.length === 0) {
        throw new Error("Veuillez sélectionner au moins un produit");
      }

      const items = selectedProducts.map(p => ({
        productId: p.id,
        productName: p.name,
        quantity: p.quantity,
        costPrice: p.costPrice,
        soldPrice: p.soldPrice
      }));

      const totalCost = items.reduce((sum, item) => sum + (item.costPrice * item.quantity), 0);
      const realCost = items.reduce((sum, item) => sum + (item.soldPrice * item.quantity), 0);

      const payload = {
        date: currentDate.toISOString(),
        items: items,
        totalCost: totalCost,
        realCost: realCost,
        restaurantId: restaurantId
      };

      if (currentServing.id) {
        await DailyServings.updateDailyServing(currentServing.id, payload);
        await fetchDailyServings();
        alert('Plan quotidien mis à jour avec succès ! 🎉');
      } else {
        const newServing = new DailyServings(
          items,
          totalCost,
          realCost,
          currentDate.toISOString(),
          restaurantId
        );
        await newServing.addDailyServing();
        alert('Plan quotidien ajouté avec succès ! 🚀');
      }
      
      resetServingForm();

    } catch (error) {
      console.error("Erreur de sauvegarde:", error);
      const errorMessage = error.message.includes("au moins un produit") 
        ? error.message 
        : `Erreur: ${error.message || 'Échec de la sauvegarde'}`;
      alert(errorMessage);
    }
  };

  // Calculate totals
  const totalRevenue = selectedProducts.reduce((sum, p) => sum + (p.quantity * p.soldPrice), 0);
  const totalCost = selectedProducts.reduce((sum, p) => sum + (p.quantity * p.costPrice), 0);
  const totalProfit = totalRevenue - totalCost;
  const profitMargin = totalRevenue > 0 ? (totalProfit / totalRevenue * 100).toFixed(1) : 0;

  const formatCurrency = (value) => {
    return value.toFixed(2) + ' DT';
  };

  return (
    <Card sx={{ 
      borderRadius: 4,
      boxShadow: '0 4px 20px rgba(0,0,0,0.08)',
      overflow: 'hidden',
      bgcolor: '#FFFFFF'
    }}>
      {/* Simple Header */}
      <Box sx={{ 
        p: 3,
        bgcolor: '#F8F9FA',
        borderBottom: '1px solid #E0E0E0',
        display: 'flex',
        alignItems: 'center',
        gap: 2
      }}>
        <Avatar sx={{ bgcolor: '#667eea', color: 'white' }}>
          <MenuBook />
        </Avatar>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 600, color: '#1A2B3C' }}>
            {currentServing.id ? 'Modifier le plan' : 'Nouveau plan de vente'}
          </Typography>
          <Typography variant="caption" sx={{ color: '#5F6B7A' }}>
            {currentServing.id ? 'Mettre à jour les quantités' : 'Enregistrer les ventes du jour'}
          </Typography>
        </Box>
      </Box>

      <CardContent sx={{ p: 3 }}>
        {/* Date Selection */}
        <LocalizationProvider dateAdapter={AdapterDateFns}>
          <Box sx={{ mb: 3 }}>
            <DatePicker
              label="Date du plan"
              value={currentDate}
              onChange={(newDate) => setCurrentDate(newDate)}
              inputFormat="dd/MM/yyyy"
              renderInput={(params) => (
                <TextField
                  {...params}
                  fullWidth
                  size="small"
                  InputProps={{
                    ...params.InputProps,
                    startAdornment: (
                      <InputAdornment position="start">
                        <Event sx={{ color: '#667eea', fontSize: 20 }} />
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      bgcolor: '#F5F7FA',
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#667eea'
                      }
                    }
                  }}
                />
              )}
            />
          </Box>
        </LocalizationProvider>

        {/* Product Selection */}
        <FormControl fullWidth size="small" sx={{ mb: 3 }}>
          <Select
            value=""
            onChange={(e) => handleProductSelect(JSON.parse(e.target.value))}
            displayEmpty
            renderValue={() => (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Restaurant sx={{ color: '#667eea', fontSize: 20 }} />
                <Typography sx={{ color: '#5F6B7A' }}>
                  Sélectionner un produit
                </Typography>
              </Box>
            )}
            sx={{
              bgcolor: '#F5F7FA',
              borderRadius: 2,
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#E0E0E0'
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#667eea'
              }
            }}
          >
            {products
              .filter(product => product.restaurantId === restaurantId)
              .map(product => (
                <MenuItem 
                  key={product.id} 
                  value={JSON.stringify(product)}
                  sx={{ py: 1 }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '100%' }}>
                    <Avatar sx={{ width: 32, height: 32, bgcolor: '#E8F5E9' }}>
                      🍽️
                    </Avatar>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {product.name}
                      </Typography>
                      <Box sx={{ display: 'flex', gap: 2 }}>
                        <Chip 
                          label={`${product.soldPrice} DT`} 
                          size="small"
                          sx={{ height: 20, fontSize: '0.65rem' }}
                        />
                      </Box>
                    </Box>
                  </Box>
                </MenuItem>
              ))}
          </Select>
        </FormControl>

        {/* Selected Items List */}
        {selectedProducts.length > 0 && (
          <motion.div
            variants={staggerContainer}
            initial="initial"
            animate="animate"
          >
            <Box sx={{ mb: 3 }}>
              <Typography variant="subtitle2" sx={{ mb: 2, color: '#1A2B3C', fontWeight: 600 }}>
                Produits sélectionnés ({selectedProducts.length})
              </Typography>
              
              <Grid container spacing={1.5}>
                <AnimatePresence>
                  {selectedProducts.map((product) => (
                    <Grid item xs={12} key={product.id}>
                      <motion.div variants={fadeInUp} exit={{ opacity: 0, x: -20 }}>
                        <Paper sx={{ 
                          p: 1.5,
                          borderRadius: 2,
                          bgcolor: '#F8F9FA',
                          border: '1px solid #E0E0E0'
                        }}>
                          <Grid container alignItems="center" spacing={1}>
                            <Grid item xs={5}>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Avatar sx={{ width: 28, height: 28, bgcolor: '#E8F5E9' }}>
                                  🍽️
                                </Avatar>
                                <Typography variant="body2" noWrap sx={{ fontWeight: 500 }}>
                                  {product.name}
                                </Typography>
                              </Box>
                            </Grid>
                            
                            <Grid item xs={4}>
                              <TextField
                                size="small"
                                type="number"
                                value={product.quantity}
                                onChange={(e) => updateQuantity(product.id, parseInt(e.target.value) || 1)}
                                InputProps={{
                                  sx: { 
                                    borderRadius: 2,
                                    bgcolor: 'white',
                                    height: 36
                                  },
                                  startAdornment: (
                                    <InputAdornment position="start">
                                      <IconButton 
                                        onClick={() => updateQuantity(product.id, Math.max(1, product.quantity - 1))}
                                        size="small"
                                        sx={{ p: 0.5 }}
                                      >
                                        <Remove fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  ),
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      <IconButton 
                                        onClick={() => updateQuantity(product.id, product.quantity + 1)}
                                        size="small"
                                        sx={{ p: 0.5 }}
                                      >
                                        <Add fontSize="small" />
                                      </IconButton>
                                    </InputAdornment>
                                  )
                                }}
                                inputProps={{ 
                                  min: 1,
                                  style: { textAlign: 'center' }
                                }}
                              />
                            </Grid>
                            
                            <Grid item xs={2}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#667eea' }}>
                                {(product.quantity * product.soldPrice).toFixed(0)} DT
                              </Typography>
                            </Grid>
                            
                            <Grid item xs={1}>
                              <IconButton
                                size="small"
                                onClick={() => removeProduct(product.id)}
                                sx={{ color: '#f44336' }}
                              >
                                <Close fontSize="small" />
                              </IconButton>
                            </Grid>
                          </Grid>
                        </Paper>
                      </motion.div>
                    </Grid>
                  ))}
                </AnimatePresence>
              </Grid>
            </Box>
          </motion.div>
        )}

        {/* Summary & Actions */}
        {selectedProducts.length > 0 && (
          <Box sx={{ 
            p: 2,
            bgcolor: '#F5F7FA',
            borderRadius: 2,
            border: '1px solid #E0E0E0'
          }}>
            <Grid container spacing={2} alignItems="center">
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block' }}>
                  Chiffre d'affaires
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: '#1A2B3C' }}>
                  {formatCurrency(totalRevenue)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block' }}>
                  Bénéfice
                </Typography>
                <Typography variant="h6" sx={{ fontWeight: 700, color: totalProfit >= 0 ? '#4CAF50' : '#f44336' }}>
                  {formatCurrency(totalProfit)}
                </Typography>
              </Grid>
              
              <Grid item xs={12} sm={4}>
                <Typography variant="caption" sx={{ color: '#5F6B7A', display: 'block' }}>
                  Marge
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography variant="h6" sx={{ fontWeight: 700, color: '#FF9800' }}>
                    {profitMargin}%
                  </Typography>
                  {profitMargin > 30 && <TrendingUp sx={{ color: '#4CAF50', fontSize: 20 }} />}
                </Box>
              </Grid>
            </Grid>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
              {currentServing.id && (
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<Cancel />}
                  onClick={resetServingForm}
                  sx={{ borderRadius: 2, textTransform: 'none' }}
                >
                  Annuler
                </Button>
              )}
              <Button
                variant="contained"
                size="small"
                startIcon={<Save />}
                onClick={handleSubmit}
                disabled={!selectedProducts.length}
                sx={{
                  borderRadius: 2,
                  textTransform: 'none',
                  bgcolor: '#667eea',
                  '&:hover': { bgcolor: '#5a6fd6' }
                }}
              >
                {currentServing.id ? 'Mettre à jour' : 'Enregistrer'}
              </Button>
            </Box>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};